import {
  PixelStreamingWrapper,
  PixelStreamingWrapperHandle,
  DisplayMode,
} from "./PixelStreamingWrapper";
import { useRef, useMemo } from "react";
import { SessionSpec } from "../utils/sessionEndpoints";
import { 
  getTheChosenCodec,
  SupportedCodecKey, 
  useCodecCapabilitiesWithHardware,
} from "../hooks/useDecoderCapabilities";
import { AllSettings } from "@epicgames-ps/lib-pixelstreamingfrontend-ue5.6";
import { useSelector } from "react-redux";
import { selectPreferredCodecOverride } from "../store/sessionSlice";

interface UseStreamerInstanceParams {
  isActive: boolean;
  displayMode: DisplayMode;
  sessionSpec: SessionSpec | null | undefined;
  sessionId?: string;
  streamToken?: string;
  pixelStreamerURL?: string;
  needsPermission: boolean;
  shouldClickToPlayContinue: () => Promise<boolean>;
  onStreamStarted: () => void;
}

interface AudioControls {
  changeDevice: (id: string) => Promise<void>;
  mute: (muted: boolean) => void;
  isMuted: () => boolean;
  setVolume: (volume: number) => void;
  getVolume: () => number;
}

interface CodecControls {
  setPreferredCodec: (codec: SupportedCodecKey) => void;
}

interface StreamerInstance {
  component: JSX.Element | null;
  audioControls: AudioControls;
  codecControls: CodecControls;
}

export function useStreamerInstance({
  isActive,
  displayMode,
  sessionSpec,
  sessionId,
  streamToken,
  pixelStreamerURL,
  needsPermission,
  shouldClickToPlayContinue,
  onStreamStarted,
}: UseStreamerInstanceParams) {
  const pixelStreamingRef = useRef<PixelStreamingWrapperHandle>(null);
  const preferredCodecOverride = useSelector(selectPreferredCodecOverride);
  const { codecs, loading, error } = useCodecCapabilitiesWithHardware();

  const component = useMemo(() => {
    if (!isActive) return null;
    if (loading) return null;

    const initialSettings = {
      AutoPlayVideo: true,
      AutoConnect: true,
      ss: pixelStreamerURL,
      StartVideoMuted: false,
      HoveringMouse: false,
      // ControlsQuality: true,
      WaitForStreamer: true,
      KeyboardInput: false,
      MouseInput: false,
      TouchInput: false,
      UseMic: false,
    } as Partial<AllSettings>;

    console.log("[StreamerInstance] useStreamerInstance codecs:", codecs, "loading:", loading, "error:", error);

    if (preferredCodecOverride) {
      initialSettings.PreferredCodec = preferredCodecOverride;
    } else if (codecs.length > 0) {
      initialSettings.PreferredCodec = getTheChosenCodec(codecs);
    }

    console.log("[StreamerInstance] setting preferred codec to:", initialSettings.PreferredCodec);

    return (
      <PixelStreamingWrapper
        key={sessionId}
        ref={pixelStreamingRef}
        displayMode={displayMode}
        audioAccessRequired={needsPermission}
        shouldClickToPlayContinue={shouldClickToPlayContinue}
        onVideoStart={onStreamStarted}
        initialSettings={initialSettings}
        sessionId={sessionId} // explicit, don't rely on key
        sessionSpec={sessionSpec}
      />
    );
  }, [
    isActive,
    displayMode,
    needsPermission,
    shouldClickToPlayContinue,
    onStreamStarted,
    pixelStreamerURL,
    sessionId,
    sessionSpec,
    codecs,
    loading,
    error,
  ]);

  const audioControls = {
    changeDevice: async (id: string) => {
      await pixelStreamingRef.current?.setAudioSpeakerId(id);
    },
    mute: (muted: boolean) => {
      pixelStreamingRef.current?.setAudioSpeakerMuted(muted);
    },
    isMuted: () => {
      return pixelStreamingRef.current?.getAudioSpeakerMuted() ?? false;
    },
    setVolume: (volume: number) => {
      pixelStreamingRef.current?.setAudioSpeakerVolume(volume);
    },
    getVolume: () => {
      return pixelStreamingRef.current?.getAudioSpeakerVolume() ?? 1.0;
    },
  };

  const codecControls = {
    setPreferredCodec: (codec: SupportedCodecKey) => {
      pixelStreamingRef.current?.setPreferredCodec(codec);
    },
  };

  return { component, audioControls, codecControls } as StreamerInstance;
}
